/**
 * VDB Ring Builder Cart Fix & Duplicate Preventer
 * Resolves Shopify's replication lag for newly created app variants by retrying failed /cart/add.js requests.
 * Integrates VDB Add to Cart success with Shopify's Cart Drawer.
 * Prevents multiple clicks, duplicate event triggers, and concurrent requests.
 */
(function() {
  let isAddingToCart = false;
  let clickTimeout = null;

  // 1. Capturing-phase Click Handler to Debounce and Prevent Multiple Triggers
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('.vdbrb-add-to-cart-btn');
    if (btn) {
      if (isAddingToCart || btn.disabled || btn.getAttribute('data-disabled') === 'true' || btn.classList.contains('clicked')) {
        console.warn('[VDB Cart Fix] Blocked duplicate click trigger.');
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }

      // Lock the button immediately
      btn.setAttribute('data-disabled', 'true');
      btn.classList.add('clicked');
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
      btn.innerHTML = 'Adding to Cart...';

      // Set safety timeout to prevent infinite button lock in case of unexpected script crash
      if (clickTimeout) clearTimeout(clickTimeout);
      clickTimeout = setTimeout(function() {
        if (isAddingToCart === false && btn.getAttribute('data-disabled') === 'true') {
          console.warn('[VDB Cart Fix] Add to cart timed out. Re-enabling button.');
          resetButtonState(btn);
        }
      }, 10000); // 10s safety timeout
    }
  }, true); // true = capturing phase (executes BEFORE jQuery delegation on body)

  function resetButtonState(btn) {
    if (!btn) btn = document.querySelector('.vdbrb-add-to-cart-btn');
    if (btn) {
      btn.innerHTML = 'Add to Cart';
      btn.removeAttribute('disabled');
      btn.removeAttribute('data-disabled');
      btn.classList.remove('clicked');
      btn.style.pointerEvents = 'auto';
      btn.style.opacity = '1';
    }
    isAddingToCart = false;
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      clickTimeout = null;
    }
  }

  // 2. Intercept Fetch API
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof URL ? args[0].href : (args[0] && args[0].url));
    if (url && (url.includes('/cart/add.js') || url.includes('/cart/add'))) {
      if (isAddingToCart) {
        console.warn('[VDB Cart Fix] Blocked duplicate fetch request.');
        return Promise.reject(new Error('VDB Cart request already in progress.'));
      }
      isAddingToCart = true;

      let attempts = 0;
      const maxAttempts = 5;
      const delay = 1000;

      function doFetch() {
        attempts++;
        return originalFetch(...args).then(response => {
          // If Shopify returns 429 (Too Many Requests), do NOT retry
          if (response.status === 429) {
            console.error('[VDB Cart Fix] Received 429 Too Many Requests. Aborting.');
            resetButtonState();
            return response;
          }
          // Shopify returns 422, 404, or 502 if variant is not found/replicated yet
          if ((response.status === 422 || response.status === 404 || response.status === 502) && attempts < maxAttempts) {
            console.warn(`[VDB Cart Fix] Fetch returned ${response.status}. Retrying in ${delay}ms... (Attempt ${attempts}/${maxAttempts})`);
            return new Promise(resolve => setTimeout(resolve, delay)).then(doFetch);
          }
          resetButtonState();
          return response;
        }).catch(error => {
          if (attempts < maxAttempts) {
            console.warn(`[VDB Cart Fix] Fetch failed with error. Retrying in ${delay}ms... (Attempt ${attempts}/${maxAttempts})`);
            return new Promise(resolve => setTimeout(resolve, delay)).then(doFetch);
          }
          resetButtonState();
          throw error;
        });
      }
      return doFetch();
    }
    return originalFetch(...args);
  };

  // 3. Intercept jQuery AJAX
  function interceptJQuery() {
    if (typeof jQuery === 'undefined') {
      setTimeout(interceptJQuery, 100);
      return;
    }

    const originalAjax = jQuery.ajax;
    jQuery.ajax = function(options) {
      const url = options.url || '';
      if (url.includes('/cart/add.js') || url.includes('/cart/add')) {
        if (isAddingToCart) {
          console.warn('[VDB Cart Fix] Blocked duplicate AJAX request.');
          // Return a mock deferred object to prevent calling code from breaking
          const d = jQuery.Deferred();
          return d.reject(null, 'abort', 'VDB Cart request already in progress.');
        }
        isAddingToCart = true;

        // Automatically request Cart Drawer sections in the response
        if (options.data) {
          if (typeof options.data === 'object') {
            options.data.sections = 'cart-drawer,cart-icon-bubble';
          } else if (typeof options.data === 'string') {
            if (!options.data.includes('sections=')) {
              options.data += '&sections=cart-drawer,cart-icon-bubble';
            }
          }
        } else {
          options.data = { sections: 'cart-drawer,cart-icon-bubble' };
        }

        const originalSuccess = options.success;
        const originalError = options.error;
        let attempts = 0;
        const maxAttempts = 5;
        const delay = 1000;

        function doAjax() {
          attempts++;
          originalAjax({
            ...options,
            success: function(result, textStatus, jqXHR) {
              console.log('[VDB Cart Fix] Add to cart succeeded:', result);
              
              // Try to update and open Cart Drawer
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && result && result.sections) {
                try {
                  cartDrawer.renderContents(result);
                  
                  // Publish cart update event to update cart bubble or other elements
                  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                      source: 'product-form',
                      productVariantId: result.id,
                      cartData: result
                    });
                  }
                  
                  // Clean up VDB local storage settings
                  if (typeof forgetAllSettings === 'function') {
                    forgetAllSettings();
                  } else if (window.forgetAllSettings) {
                    window.forgetAllSettings();
                  }
                  
                  resetButtonState();
                  return; // Skip VDB redirect to keep user on the page and show drawer
                } catch (e) {
                  console.error('[VDB Cart Fix] Error updating cart drawer:', e);
                }
              }
              
              resetButtonState();
              // Fallback to original success callback (which redirects to /cart)
              if (originalSuccess) originalSuccess(result, textStatus, jqXHR);
            },
            error: function(jqXHR, textStatus, errorThrown) {
              // If Shopify returns 429, immediately abort retrying
              if (jqXHR.status === 429) {
                console.error('[VDB Cart Fix] Received 429 Too Many Requests via AJAX. Aborting.');
                resetButtonState();
                if (originalError) originalError(jqXHR, textStatus, errorThrown);
                return;
              }

              if ((jqXHR.status === 422 || jqXHR.status === 404 || jqXHR.status === 502) && attempts < maxAttempts) {
                console.warn(`[VDB Cart Fix] jQuery $.ajax returned ${jqXHR.status}. Retrying in ${delay}ms... (Attempt ${attempts}/${maxAttempts})`);
                setTimeout(doAjax, delay);
              } else {
                console.error(`[VDB Cart Fix] Add to cart failed after ${attempts} attempts:`, jqXHR.responseText);
                resetButtonState();
                if (originalError) originalError(jqXHR, textStatus, errorThrown);
              }
            }
          });
        }
        doAjax();
        return; // Prevents the original call from running in parallel
      }
      return originalAjax.apply(this, arguments);
    };
    console.log('[VDB Cart Fix] jQuery $.ajax interceptor installed.');
  }
  interceptJQuery();
})();
